export type OrgTemplateSeedId =
  | 'org-template-hierarchical'
  | 'org-template-horizontal'
  | 'org-template-flat'
  | 'org-template-functional'
  | 'org-template-divisional'
  | 'org-template-matrix'
  | 'org-template-project-based'
  | 'org-template-team-based';

export type OrgTemplateGroupId =
  | 'core-structures'
  | 'department-models'
  | 'cross-functional';

export type OrgTemplateVignetteId =
  | 'org'
  | 'team-org-chart'
  | 'department-org-chart'
  | 'reorg-plan';

export interface OrgTemplateNodeSpec {
  id: string;
  name: string;
  role: string;
  x: number;
  y: number;
  accentColor: string;
}

export interface OrgTemplateEdgeSpec {
  fromId: string;
  toId: string;
  relationshipType?: 'dotted_line_reports' | 'matrix_reports';
  dotted?: boolean;
  visible?: boolean;
  fromAnchor?: 'top' | 'right' | 'bottom' | 'left';
  toAnchor?: 'top' | 'right' | 'bottom' | 'left';
}

export interface OrgTemplateSeedSpec {
  id: OrgTemplateSeedId;
  title: string;
  description: string;
  vignetteId: OrgTemplateVignetteId;
  imageSrc: string;
  imageAlt: string;
  nodes: OrgTemplateNodeSpec[];
  edges: OrgTemplateEdgeSpec[];
}

export interface OrgTemplateGroupSpec {
  id: OrgTemplateGroupId;
  title: string;
  description: string;
  templateIds: readonly OrgTemplateSeedId[];
}

const compass = {
  coral: '#F4604C',
  navy: '#0A1F3D',
  navySoft: '#466493',
  teal: '#0F766E',
  amber: '#D97706',
  slate: '#5B6B85',
};

export const ORG_TEMPLATE_GROUPS: readonly OrgTemplateGroupSpec[] = [
  {
    id: 'core-structures',
    title: 'Core structures',
    description: 'Simple manual starters for everyday reporting lines and lightweight team maps.',
    templateIds: ['org-template-hierarchical', 'org-template-horizontal', 'org-template-flat'],
  },
  {
    id: 'department-models',
    title: 'Department models',
    description: 'Department, division, and squad views for planning or imported people data.',
    templateIds: ['org-template-functional', 'org-template-divisional', 'org-template-team-based'],
  },
  {
    id: 'cross-functional',
    title: 'Cross-functional',
    description: 'Planning structures for matrix reporting, project work, and secondary relationships.',
    templateIds: ['org-template-matrix', 'org-template-project-based'],
  },
];

export const ORG_TEMPLATE_SEED_SPECS: OrgTemplateSeedSpec[] = [
  {
    id: 'org-template-hierarchical',
    title: 'Hierarchical Org Chart',
    description: 'Classic top-down reporting structure for a growing team.',
    vignetteId: 'org',
    imageSrc: '/assets/images/org-templates/compass-hierarchical-org-chart.png',
    imageAlt: 'Compass hierarchical org chart template preview',
    nodes: [
      { id: 'hier-ceo', name: 'Maya Chen', role: 'CEO', x: 520, y: 100, accentColor: compass.coral },
      { id: 'hier-product', name: 'Alex Rivera', role: 'Head of Product', x: 220, y: 260, accentColor: compass.teal },
      { id: 'hier-sales', name: 'Jordan Lee', role: 'Head of Sales', x: 520, y: 260, accentColor: compass.navySoft },
      { id: 'hier-ops', name: 'Sam Patel', role: 'Head of Operations', x: 820, y: 260, accentColor: compass.amber },
      { id: 'hier-priya', name: 'Priya Shah', role: 'Product Manager', x: 70, y: 420, accentColor: compass.teal },
      { id: 'hier-noah', name: 'Noah Kim', role: 'Product Designer', x: 300, y: 420, accentColor: compass.teal },
      { id: 'hier-mia', name: 'Mia Torres', role: 'Sales Manager', x: 520, y: 420, accentColor: compass.navySoft },
      { id: 'hier-leo', name: 'Leo Brooks', role: 'Account Executive', x: 740, y: 420, accentColor: compass.navySoft },
      { id: 'hier-ava', name: 'Ava Singh', role: 'Operations Lead', x: 960, y: 420, accentColor: compass.amber },
    ],
    edges: [
      { fromId: 'hier-product', toId: 'hier-ceo' },
      { fromId: 'hier-sales', toId: 'hier-ceo' },
      { fromId: 'hier-ops', toId: 'hier-ceo' },
      { fromId: 'hier-priya', toId: 'hier-product' },
      { fromId: 'hier-noah', toId: 'hier-product' },
      { fromId: 'hier-mia', toId: 'hier-sales' },
      { fromId: 'hier-leo', toId: 'hier-sales' },
      { fromId: 'hier-ava', toId: 'hier-ops' },
    ],
  },
  {
    id: 'org-template-horizontal',
    title: 'Horizontal Org Chart',
    description: 'Left-to-right reporting flow for compact storytelling.',
    vignetteId: 'org',
    imageSrc: '/assets/images/org-templates/compass-horizontal-org-chart.png',
    imageAlt: 'Compass horizontal org chart template preview',
    nodes: [
      { id: 'horiz-ceo', name: 'Maya Chen', role: 'CEO', x: 90, y: 310, accentColor: compass.coral },
      { id: 'horiz-product', name: 'Alex Rivera', role: 'Product', x: 420, y: 150, accentColor: compass.teal },
      { id: 'horiz-sales', name: 'Jordan Lee', role: 'Sales', x: 420, y: 310, accentColor: compass.navySoft },
      { id: 'horiz-ops', name: 'Sam Patel', role: 'Operations', x: 420, y: 470, accentColor: compass.amber },
      { id: 'horiz-priya', name: 'Priya Shah', role: 'PM', x: 760, y: 70, accentColor: compass.teal },
      { id: 'horiz-noah', name: 'Noah Kim', role: 'Design', x: 760, y: 210, accentColor: compass.teal },
      { id: 'horiz-mia', name: 'Mia Torres', role: 'Sales Manager', x: 760, y: 310, accentColor: compass.navySoft },
      { id: 'horiz-ava', name: 'Ava Singh', role: 'Ops Lead', x: 760, y: 470, accentColor: compass.amber },
    ],
    edges: [
      { fromId: 'horiz-product', toId: 'horiz-ceo', fromAnchor: 'left', toAnchor: 'right' },
      { fromId: 'horiz-sales', toId: 'horiz-ceo', fromAnchor: 'left', toAnchor: 'right' },
      { fromId: 'horiz-ops', toId: 'horiz-ceo', fromAnchor: 'left', toAnchor: 'right' },
      { fromId: 'horiz-priya', toId: 'horiz-product', fromAnchor: 'left', toAnchor: 'right' },
      { fromId: 'horiz-noah', toId: 'horiz-product', fromAnchor: 'left', toAnchor: 'right' },
      { fromId: 'horiz-mia', toId: 'horiz-sales', fromAnchor: 'left', toAnchor: 'right' },
      { fromId: 'horiz-ava', toId: 'horiz-ops', fromAnchor: 'left', toAnchor: 'right' },
    ],
  },
  {
    id: 'org-template-flat',
    title: 'Flat Org Chart',
    description: 'Small team view with one lead and a broad peer layer.',
    vignetteId: 'team-org-chart',
    imageSrc: '/assets/images/org-templates/compass-flat-org-chart.png',
    imageAlt: 'Compass flat org chart template preview',
    nodes: [
      { id: 'flat-lead', name: 'Maya Chen', role: 'Founder', x: 520, y: 120, accentColor: compass.coral },
      { id: 'flat-product', name: 'Alex Rivera', role: 'Product', x: 100, y: 320, accentColor: compass.teal },
      { id: 'flat-design', name: 'Noah Kim', role: 'Design', x: 310, y: 320, accentColor: compass.teal },
      { id: 'flat-sales', name: 'Jordan Lee', role: 'Sales', x: 520, y: 320, accentColor: compass.navySoft },
      { id: 'flat-success', name: 'Mia Torres', role: 'Success', x: 730, y: 320, accentColor: compass.navySoft },
      { id: 'flat-ops', name: 'Sam Patel', role: 'Operations', x: 940, y: 320, accentColor: compass.amber },
    ],
    edges: [
      { fromId: 'flat-product', toId: 'flat-lead' },
      { fromId: 'flat-design', toId: 'flat-lead' },
      { fromId: 'flat-sales', toId: 'flat-lead' },
      { fromId: 'flat-success', toId: 'flat-lead' },
      { fromId: 'flat-ops', toId: 'flat-lead' },
    ],
  },
  {
    id: 'org-template-functional',
    title: 'Functional Org Chart',
    description: 'Departments grouped by function under one executive layer.',
    vignetteId: 'department-org-chart',
    imageSrc: '/assets/images/org-templates/compass-functional-org-chart.png',
    imageAlt: 'Compass functional org chart template preview',
    nodes: [
      { id: 'func-ceo', name: 'Maya Chen', role: 'CEO', x: 520, y: 80, accentColor: compass.coral },
      { id: 'func-product', name: 'Product', role: 'Department Lead', x: 100, y: 240, accentColor: compass.teal },
      { id: 'func-sales', name: 'Sales', role: 'Department Lead', x: 380, y: 240, accentColor: compass.navySoft },
      { id: 'func-finance', name: 'Finance', role: 'Department Lead', x: 660, y: 240, accentColor: compass.slate },
      { id: 'func-people', name: 'People', role: 'Department Lead', x: 940, y: 240, accentColor: compass.amber },
      { id: 'func-designer', name: 'Designer', role: 'Product Team', x: 100, y: 400, accentColor: compass.teal },
      { id: 'func-ae', name: 'Account Exec', role: 'Sales Team', x: 380, y: 400, accentColor: compass.navySoft },
      { id: 'func-analyst', name: 'Analyst', role: 'Finance Team', x: 660, y: 400, accentColor: compass.slate },
      { id: 'func-recruiter', name: 'Recruiter', role: 'People Team', x: 940, y: 400, accentColor: compass.amber },
    ],
    edges: [
      { fromId: 'func-product', toId: 'func-ceo' },
      { fromId: 'func-sales', toId: 'func-ceo' },
      { fromId: 'func-finance', toId: 'func-ceo' },
      { fromId: 'func-people', toId: 'func-ceo' },
      { fromId: 'func-designer', toId: 'func-product' },
      { fromId: 'func-ae', toId: 'func-sales' },
      { fromId: 'func-analyst', toId: 'func-finance' },
      { fromId: 'func-recruiter', toId: 'func-people' },
    ],
  },
  {
    id: 'org-template-divisional',
    title: 'Divisional Org Chart',
    description: 'Business units or regions arranged as parallel divisions.',
    vignetteId: 'department-org-chart',
    imageSrc: '/assets/images/org-templates/compass-divisional-org-chart.png',
    imageAlt: 'Compass divisional org chart template preview',
    nodes: [
      { id: 'div-ceo', name: 'Maya Chen', role: 'CEO', x: 520, y: 70, accentColor: compass.coral },
      { id: 'div-na', name: 'North America', role: 'Division Lead', x: 80, y: 230, accentColor: compass.teal },
      { id: 'div-emea', name: 'EMEA', role: 'Division Lead', x: 360, y: 230, accentColor: compass.navySoft },
      { id: 'div-apac', name: 'APAC', role: 'Division Lead', x: 640, y: 230, accentColor: compass.amber },
      { id: 'div-platform', name: 'Platform', role: 'Division Lead', x: 920, y: 230, accentColor: compass.slate },
      { id: 'div-na-ops', name: 'NA Ops', role: 'Operations', x: 80, y: 390, accentColor: compass.teal },
      { id: 'div-emea-sales', name: 'EMEA Sales', role: 'Sales', x: 360, y: 390, accentColor: compass.navySoft },
      { id: 'div-apac-growth', name: 'APAC Growth', role: 'Growth', x: 640, y: 390, accentColor: compass.amber },
      { id: 'div-platform-eng', name: 'Platform Eng', role: 'Engineering', x: 920, y: 390, accentColor: compass.slate },
    ],
    edges: [
      { fromId: 'div-na', toId: 'div-ceo' },
      { fromId: 'div-emea', toId: 'div-ceo' },
      { fromId: 'div-apac', toId: 'div-ceo' },
      { fromId: 'div-platform', toId: 'div-ceo' },
      { fromId: 'div-na-ops', toId: 'div-na' },
      { fromId: 'div-emea-sales', toId: 'div-emea' },
      { fromId: 'div-apac-growth', toId: 'div-apac' },
      { fromId: 'div-platform-eng', toId: 'div-platform' },
    ],
  },
  {
    id: 'org-template-matrix',
    title: 'Matrix Org Chart',
    description: 'Solid-line managers plus dotted project relationships.',
    vignetteId: 'reorg-plan',
    imageSrc: '/assets/images/org-templates/compass-matrix-org-chart.png',
    imageAlt: 'Compass matrix org chart template preview',
    nodes: [
      { id: 'matrix-ceo', name: 'Maya Chen', role: 'Executive Sponsor', x: 520, y: 70, accentColor: compass.coral },
      { id: 'matrix-product', name: 'Product Lead', role: 'Function', x: 240, y: 230, accentColor: compass.teal },
      { id: 'matrix-design', name: 'Design Lead', role: 'Function', x: 520, y: 230, accentColor: compass.navySoft },
      { id: 'matrix-engineering', name: 'Engineering Lead', role: 'Function', x: 800, y: 230, accentColor: compass.slate },
      { id: 'matrix-project', name: 'Project Lead', role: 'Launch Team', x: 640, y: 400, accentColor: compass.amber },
      { id: 'matrix-priya', name: 'Priya Shah', role: 'PM', x: 240, y: 560, accentColor: compass.teal },
      { id: 'matrix-noah', name: 'Noah Kim', role: 'Designer', x: 520, y: 560, accentColor: compass.navySoft },
      { id: 'matrix-ava', name: 'Ava Singh', role: 'Engineer', x: 800, y: 560, accentColor: compass.slate },
    ],
    edges: [
      { fromId: 'matrix-product', toId: 'matrix-ceo' },
      { fromId: 'matrix-design', toId: 'matrix-ceo' },
      { fromId: 'matrix-engineering', toId: 'matrix-ceo' },
      { fromId: 'matrix-project', toId: 'matrix-ceo', relationshipType: 'matrix_reports', dotted: true, visible: true },
      { fromId: 'matrix-priya', toId: 'matrix-product' },
      { fromId: 'matrix-noah', toId: 'matrix-design' },
      { fromId: 'matrix-ava', toId: 'matrix-engineering' },
      { fromId: 'matrix-priya', toId: 'matrix-project', relationshipType: 'matrix_reports', dotted: true, visible: true, fromAnchor: 'right', toAnchor: 'left' },
      { fromId: 'matrix-noah', toId: 'matrix-project', relationshipType: 'matrix_reports', dotted: true, visible: true, fromAnchor: 'right', toAnchor: 'left' },
      { fromId: 'matrix-ava', toId: 'matrix-project', relationshipType: 'matrix_reports', dotted: true, visible: true, fromAnchor: 'left', toAnchor: 'right' },
    ],
  },
  {
    id: 'org-template-project-based',
    title: 'Project-Based Org Chart',
    description: 'Project leads and delivery squads grouped around workstreams.',
    vignetteId: 'reorg-plan',
    imageSrc: '/assets/images/org-templates/compass-project-based-org-chart.png',
    imageAlt: 'Compass project-based org chart template preview',
    nodes: [
      { id: 'project-portfolio', name: 'Maya Chen', role: 'Portfolio Lead', x: 520, y: 80, accentColor: compass.coral },
      { id: 'project-alpha', name: 'Alpha Project', role: 'Project Lead', x: 240, y: 250, accentColor: compass.teal },
      { id: 'project-beta', name: 'Beta Project', role: 'Project Lead', x: 520, y: 250, accentColor: compass.navySoft },
      { id: 'project-gamma', name: 'Gamma Project', role: 'Project Lead', x: 800, y: 250, accentColor: compass.amber },
      { id: 'project-alpha-a', name: 'Alpha Design', role: 'Squad Member', x: 100, y: 420, accentColor: compass.teal },
      { id: 'project-alpha-b', name: 'Alpha Build', role: 'Squad Member', x: 320, y: 420, accentColor: compass.teal },
      { id: 'project-beta-a', name: 'Beta Ops', role: 'Squad Member', x: 520, y: 420, accentColor: compass.navySoft },
      { id: 'project-gamma-a', name: 'Gamma Growth', role: 'Squad Member', x: 800, y: 420, accentColor: compass.amber },
    ],
    edges: [
      { fromId: 'project-alpha', toId: 'project-portfolio' },
      { fromId: 'project-beta', toId: 'project-portfolio' },
      { fromId: 'project-gamma', toId: 'project-portfolio' },
      { fromId: 'project-alpha-a', toId: 'project-alpha' },
      { fromId: 'project-alpha-b', toId: 'project-alpha' },
      { fromId: 'project-beta-a', toId: 'project-beta' },
      { fromId: 'project-gamma-a', toId: 'project-gamma' },
    ],
  },
  {
    id: 'org-template-team-based',
    title: 'Team-Based Org Chart',
    description: 'Team leads with small squads, good for early planning.',
    vignetteId: 'team-org-chart',
    imageSrc: '/assets/images/org-templates/compass-team-based-org-chart.png',
    imageAlt: 'Compass team-based org chart template preview',
    nodes: [
      { id: 'team-director', name: 'Maya Chen', role: 'Director', x: 520, y: 80, accentColor: compass.coral },
      { id: 'team-growth', name: 'Growth Team', role: 'Team Lead', x: 200, y: 250, accentColor: compass.teal },
      { id: 'team-core', name: 'Core Team', role: 'Team Lead', x: 520, y: 250, accentColor: compass.navySoft },
      { id: 'team-platform', name: 'Platform Team', role: 'Team Lead', x: 840, y: 250, accentColor: compass.amber },
      { id: 'team-growth-a', name: 'Growth IC', role: 'Member', x: 60, y: 420, accentColor: compass.teal },
      { id: 'team-growth-b', name: 'Lifecycle IC', role: 'Member', x: 340, y: 420, accentColor: compass.teal },
      { id: 'team-core-a', name: 'Core IC', role: 'Member', x: 520, y: 420, accentColor: compass.navySoft },
      { id: 'team-platform-a', name: 'Platform IC', role: 'Member', x: 840, y: 420, accentColor: compass.amber },
    ],
    edges: [
      { fromId: 'team-growth', toId: 'team-director' },
      { fromId: 'team-core', toId: 'team-director' },
      { fromId: 'team-platform', toId: 'team-director' },
      { fromId: 'team-growth-a', toId: 'team-growth' },
      { fromId: 'team-growth-b', toId: 'team-growth' },
      { fromId: 'team-core-a', toId: 'team-core' },
      { fromId: 'team-platform-a', toId: 'team-platform' },
    ],
  },
];
